import { Link, useLocation } from 'react-router-dom'
import { Moon, Search, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { UserAvatar } from '@/components/common/badges'
import { useUiState } from '@/hooks/use-ui-state'
import { viewByPath } from '@/lib/views'
import { appConfig } from '@/lib/query'
import { signOut } from '@/lib/api'
import { useMe } from '@/hooks/use-me'

function Breadcrumb() {
  const { pathname } = useLocation()
  const view = viewByPath(pathname)
  if (!view) return null
  return (
    <nav className="text-muted-foreground hidden items-center gap-1.5 text-xs md:flex">
      <span>/</span>
      <span className="text-foreground font-medium">{view.title}</span>
    </nav>
  )
}

function CmdKTrigger() {
  const { setPaletteOpen } = useUiState()
  const isMac = navigator.platform.toUpperCase().includes('MAC')
  return (
    <button
      type="button"
      onClick={() => setPaletteOpen(true)}
      className="border-border bg-surface hover:border-border-strong hover:bg-card text-muted-foreground hidden h-7 w-80 items-center gap-2 rounded-md border px-2.5 text-xs transition-colors md:flex"
    >
      <Search className="size-3.5" />
      <span className="text-faint-foreground flex-1 text-left">Search issues, views…</span>
      <kbd className="bg-card border-border text-faint-foreground rounded border px-1 font-mono text-[10px]">
        {isMac ? '⌘' : 'Ctrl'} K
      </kbd>
    </button>
  )
}

function UserMenu() {
  const me = useMe()!
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 px-2">
          <UserAvatar user={{ login: me.login, name: me.name, avatarUrl: me.avatarUrl }} />
          <span className="hidden text-xs font-medium lg:inline">{me.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="text-xs">
            <p className="text-muted-foreground">Signed in as</p>
            <p className="font-semibold">{me.name}</p>
            <p className="text-faint-foreground">@{me.login}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="font-normal">
          <div className="text-xs">
            <p className="text-muted-foreground">Scope</p>
            <p className="text-muted-foreground font-mono text-[11px]">
              {appConfig.owner}/{appConfig.repo} · org issue fields
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void signOut()}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function Topbar() {
  const { theme, setTheme } = useTheme()
  const mod = navigator.platform.toUpperCase().includes('MAC') ? '⌘' : 'Ctrl'
  return (
    <header className="border-border bg-background flex h-12 shrink-0 items-center gap-2.5 border-b px-3">
      <SidebarTrigger title={`Toggle sidebar (${mod}B)`} />
      <Link to="/" className="flex items-center gap-2">
        <img src="/logo.svg" alt="" className="size-5" />
        <span className="text-[13.5px] font-semibold tracking-tight">N3O Work</span>
      </Link>
      <Breadcrumb />
      <div className="flex flex-1 justify-center">
        <CmdKTrigger />
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        title="Toggle theme"
        aria-label="Toggle theme"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        <Sun className="size-4 dark:hidden" />
        <Moon className="hidden size-4 dark:block" />
      </Button>
      <UserMenu />
    </header>
  )
}
